/*
 * ==============================================================================
 *
 * Copyright (c) 2023. Koninklijke Philips Electronics N.V.
 *
 * All rights are reserved. Reproduction in whole or in part is prohibited without the prior written consent of the copyright holder.
 *
 * ==============================================================================
 */

import React, { useEffect, useState } from "react";
import MFRData from "./MFRData";
import Note from "./note";
import MFRReport from "./MFRReport";
import Esign from "./Esign";
import { useParams } from "react-router-dom";
import { useContext } from "react";
import { LayoutContext } from "../components/layout";
import Loading from "./Loading";
import { finalReportViewService } from "../stats/FinalReportViewService";
import Loader from "react-loader-spinner";
import pdfContext from "./pdfContext";
import { featureCheck } from "../utils/feature";
import PracticeEscalationCriteria from "./PracticeEscalationCriteria";
import fetchHelper from "../services/fetchHelper";
import MimeTypes from "../constants/MimeTypes";
import { SpecialZoomLevel } from "@react-pdf-viewer/core";
import { REQUEST_TYPE_PDF } from "../components/Modals/constants";
import {useToasts} from "react-toast-notifications";

const Content = () => {
  const { user: userInfoLayout, headerDetails, mfrData: userInfo } = useContext(LayoutContext);
  const [leftTabContent, setLeftTabContent] = useState(0);
  const [rightTabContent, setRightTabContent] = useState(1);
  const [showFinalReportView, setShowFinalReportView] = useState(false);
  const [offSet, setOffSet] = useState(0);
  const [numberOfPages, setNumberOfPages] = useState(0);
  const [mfrReportPdf, setMfrReportPdf] = useState(null);
  const [escalationCriteriaPdf, setEscalationCriteriaPdf] = useState(null);
  const [shouldShowEscalationCriteriaTab, setShouldEscalationCriteriaTab] = useState(
    featureCheck("escalationCriteria.enable") && escalationCriteriaPdf
  );
  const [shouldReloadMfrReport, setShouldReloadMfrReport] = useState(false);
  const [attachmentSize, setAttachmentSize] = useState(0);
  const { addToast } = useToasts();

  const { i: interrogationId } = useParams();

  useEffect(() => {
    getEscalationCriteria();
    if (headerDetails && headerDetails.interrogationStatus === "FINAL") {
      setShowFinalReportView(true);
    }
    const subscription = finalReportViewService.getMessage().subscribe((pdfGenerationId) => {
      setShowFinalReportView(false);
      setShouldReloadMfrReport(true);
      checkStatusForFinalReport(pdfGenerationId.text);
    }, []);
    return () => {
      subscription.unsubscribe();
    }
  }, []);

  useEffect(() => {
    setShouldEscalationCriteriaTab(featureCheck("escalationCriteria.enable") && escalationCriteriaPdf);
  }, [escalationCriteriaPdf]);

  const checkStatusForFinalReport = async (pdfGenerationId) => {
    try {
      const response = await fetchHelper.get({
        url: `/patientmanagement/rest/PdfGenerator/${pdfGenerationId}/status`,
      });
      if (response.status === "SUCCESSFUL") {
        addToast("The PDF was successfully generated!", {
          appearance: "success",
        });
        setShowFinalReportView(true);
      } else {
        addToast(
          "There was a problem while generating the pdf for the interrogation id " + interrogationId + ".",
          {
            appearance: "error",
          }
        );
      }
    } catch ({errors}) {
      errors && errors.forEach((err) => {
        addToast(err.message, {
          appearance: "error",
          autoDismiss: false,
        });
      });
    }
  };

  useEffect(() => {
    if (shouldReloadMfrReport && showFinalReportView && headerDetails.interrogationStatus === "FINAL") {
      getMFRReportPDF();
      setShouldReloadMfrReport(false);
    }
  }, [shouldReloadMfrReport, showFinalReportView]);

  const getEscalationCriteria = async () => {
    try {
      const buffer = await fetchHelper.getFile({
        url: `/patientmanagement/rest/interrogations/${interrogationId}/download/escalationCriteria`,
        headers: { "Content-Type": MimeTypes.PDF },
        arrayBuffer: true,
      });

      if (buffer.byteLength === 0) {
        setEscalationCriteriaPdf(null);
      } else {
        const blob = new Blob([buffer], { type: MimeTypes.PDF });
        setEscalationCriteriaPdf(URL.createObjectURL(blob));
      }
    } catch {
      setEscalationCriteriaPdf(null);
    }
  };

  const getMFRReportPDF = async (requestType = REQUEST_TYPE_PDF.DEFAULT, times = 1, totalPages = 0) => {
    try {
      requestType = showFinalReportView ? REQUEST_TYPE_PDF.FINAL : requestType;
      const pdfQueueMicroservice = await fetchHelper.getPdfQueueStatus(headerDetails.location.id);
      let extraParams = pdfQueueMicroservice ? `/${requestType}` : "";

      const buffer = await fetchHelper.getFile({
        url: `/patientmanagement/rest/interrogations/${interrogationId}/download${extraParams}`,
        headers: { "Content-Type": MimeTypes.PDF },
        arrayBuffer: true,
      });

      setAttachmentSize(totalPages);

      if (buffer.byteLength === 0) {
        if (times === 1 && pdfQueueMicroservice) {
          console.log(`Report failed, retry again: ${times}`);

          getMFRReportPDF(requestType, times + 1, totalPages);
        }
        setMfrReportPdf(null);
      } else {
        const blob = new Blob([buffer], { type: MimeTypes.PDF });
        setMfrReportPdf(URL.createObjectURL(blob));
      }
    } catch {
      setMfrReportPdf(null);
    }
  };

  const loading = () => {
    return (
      <>
        <Loading />
        <Loading />
        <Loading />
      </>
    );
  };

  const renderLeftContent = () => {
    if (!userInfo) {
      if (!userInfoLayout && leftTabContent === 1) return <div className="tab-content">{loading()}</div>;
    }

    if (headerDetails?.interrogationStatus === "FINAL") {
      return (
        <MFRReport
          getMFRReportPDF={getMFRReportPDF}
          attachmentSize={attachmentSize}
          mfrReportPdf={mfrReportPdf}
          pdfZoomScale={SpecialZoomLevel.PageWidth}
        />
      );
    }

    switch (leftTabContent) {
      case 1:
        return <MFRData />;
      case 2:
        return <PracticeEscalationCriteria escalationCriteriaPdf={escalationCriteriaPdf} />;
      default:
        return (
          <MFRReport
            getMFRReportPDF={getMFRReportPDF}
            attachmentSize={attachmentSize}
            mfrReportPdf={mfrReportPdf}
            pdfZoomScale={SpecialZoomLevel.PageWidth}
          />
        );
    }
  };

  const renderRightContent = () => {
    if (!headerDetails || !userInfoLayout) {
      return loading();
    }

    switch (rightTabContent) {
      case 1:
        // return <Esign userInfo={headerDetails} />;
        return <Esign />;
      default:
        return <Note />;
    }
  };

  return (
    <pdfContext.Provider value={{ offSet, setOffSet, numberOfPages, setNumberOfPages }}>
      <div>
        {headerDetails && headerDetails.interrogationStatus === "FINAL" && !showFinalReportView ? (
          <div>
            <Loader
              type="ThreeDots"
              color="#008c98"
              height={100}
              width={100}
              style={{ marginLeft: "700px" }}
            />
            <span
              id="report-generation-in-progress-message"
              style={{ marginLeft: "520px", color: "#008c98" }}
            >
              Final Report generation is in progress, do not reload the current page
            </span>
          </div>
        ) : null}
        {headerDetails && headerDetails.interrogationStatus === "FINAL" && showFinalReportView ? (
          <div className="row row-stretch content-main">
            <div className="col col-60 mfr" style={{ marginBottom: 48 }}></div>
            <div
              className="col col-60 mfr"
              style={{ paddingRight: "0px" }}
              key={`content-${interrogationId}`}
            >
              <nav className="tabs ">
                <a
                  id="mfr-report-section"
                  style={{ marginRight: "0px" }}
                  href="#"
                  className={leftTabContent == 0 ? "tab-active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setLeftTabContent(0);
                  }}
                >
                  MFR Report
                </a>

                {shouldShowEscalationCriteriaTab && headerDetails?.interrogationStatus !== "FINAL" && (
                  <a
                    href="#"
                    id="escalation-criteria-section"
                    className={leftTabContent == 2 ? "tab-active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      setLeftTabContent(2);
                    }}
                  >
                    Practice Escalation Criteria
                  </a>
                )}
              </nav>
              {renderLeftContent()}
            </div>
          </div>
        ) : headerDetails && headerDetails.interrogationStatus !== "FINAL" ? (
          <div className="row row-stretch content-main">
            <div className="col col-60 mfr" style={{ marginBottom: 48 }}></div>
            <div className="col col-60 mfr" key={`content-${interrogationId}`}>
              <nav className="tabs ">
                <a
                  id="mfr-report-section"
                  href="#"
                  className={leftTabContent == 0 ? "tab-active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setLeftTabContent(0);
                  }}
                >
                  MFR Report
                </a>
                {shouldShowEscalationCriteriaTab && (
                  <a
                    href="#"
                    id="escalation-criteria-section"
                    className={leftTabContent == 2 ? "tab-active" : ""}
                    onClick={(e) => {
                      e.preventDefault();
                      setLeftTabContent(2);
                    }}
                  >
                    Practice Escalation Criteria
                  </a>
                )}
                <a
                  href="#"
                  id="mfr-data-section"
                  className={leftTabContent == 1 ? "tab-active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setLeftTabContent(1);
                  }}
                >
                  MFR Data
                </a>
              </nav>{" "}
              {renderLeftContent()}
            </div>
            <div className="col col-40 sb">
              <nav className="tabs" id="report-navigation">
                <a
                  id="note-section"
                  href="#"
                  className={rightTabContent == 0 ? "tab-active" : "" + " no-print"}
                  onClick={(e) => {
                    e.preventDefault();
                    setRightTabContent(0);
                  }}
                >
                  <i className="ti ti-notepad"></i>
                  &nbsp;Note
                </a>
                <a
                  id="esign-section"
                  href="#"
                  className={rightTabContent == 1 ? "tab-active" : ""}
                  onClick={(e) => {
                    e.preventDefault();
                    setRightTabContent(1);
                  }}
                >
                  <i className="ti ti-pencil-alt"></i> &nbsp;Esign
                </a>
              </nav>
              <div className="tab-content">{renderRightContent()}</div>
            </div>
          </div>
        ) : null}
      </div>
    </pdfContext.Provider>
  );
};

export default Content;
